import React, {useEffect, useRef, useState} from "react";
import NavBar from "../components/NavBar";
import Footer from "../components/Footer";
import {Button, Container} from "reactstrap";
import {useAuth0, withAuthenticationRequired} from "@auth0/auth0-react";
import Loading from "../components/Loading";
import {
    Avatar,
    AvatarGroup, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
    IconButton,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText, Rating, Tooltip
} from "@mui/material";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {firebaseService} from "../services/firebaseService";
import OnlineTag from "../components/OnlineTag";
import {getToken} from "firebase/messaging";
import {messaging} from "../firebase";

export const Activity = () => {
    const [usersActivityRaw, setUsersActivityRaw] = useState([]);
    const [usersActivityLoaded, setUsersActivityLoaded] = useState(false);
    const [loadingActivity, setLoadingActivity] = useState(false);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [activityToRemove, setActivityToRemove] = useState(null);

    const [userInfoDialogOpen, setUserInfoDialogOpen] = useState(false);
    const [userInfoToShow, setUserInfoToShow] = useState(null);
    const [userInfoStatsToShow, setUserInfoStatsToShow] = useState(null);

    const {user} = useAuth0();

    const deleteActivity = async (id) => {
        await firebaseService.deleteActivity(id);

        setActivityToRemove(null);
        setConfirmDialogOpen(false)

        getActivity().then((data) => {
            setUsersActivityRaw(data);
        })
    }

    const getActivity = async (loading = false) => {
        if (loading) {
            setLoadingActivity(true);
        }

        let userDetails = await firebaseService.getUserDetails(user);

        let lastOnline = null;

        if (userDetails.length === 0) {
            await firebaseService.addUser(user);
            userDetails = await firebaseService.getUserDetails(user);
            lastOnline = userDetails.lastOnline.toDate();
        } else {
            lastOnline = userDetails.lastOnline.toDate();
            let now = new Date();
            let diff = now.getTime() - lastOnline.getTime();

            let diffMinutes = Math.round(diff / 60000);

            if (diffMinutes > 5) {
                await firebaseService.updateUser(userDetails, {
                    lastOnline: new Date()
                });
            }
        }

        setUsersActivityLoaded(true);

        if (userDetails.length !== 0) {
            let data = await firebaseService.getActivities(userDetails);
            let organizedData = [];
            let currentDay = null;
            let currentActivities = [];

            data.forEach((activity) => {
                let activityDate = activity.activityDate.toDate();
                let activityDay = activityDate.toDateString();

                if (currentDay === null) {
                    currentDay = activityDay;
                }

                if (activityDay !== currentDay) {
                    organizedData.push({date: currentDay, activities: currentActivities});
                    currentActivities = [];
                    currentDay = activityDay;
                }

                if (activityDate > lastOnline) {
                    activity.new = true;
                }

                currentActivities.push(activity);
            });

            organizedData.push({date: currentDay, activities: currentActivities});

            return organizedData;
        }

        return [];
    }

    const requestPermission = async () => {
        const permission = await Notification.requestPermission();
        if (permission === "granted") {
            // Generate Device Token for notification
            const token = await getToken(messaging, {
                vapidKey:  "BFuCoVdOyvBmqkXCSIYs2TlFQ7_2kGXLxBwydjv4qohHo4D3XHIn-5stfdyAWOApfO1VqAI3jltqDQmVXPnTpoQ",
            });
            return token;
        } else if (permission === "denied") {
            console.log("Denied for the notification");
            return null;
        }
    }

    const updateUserMessageToken = async (token) => {
        if (!token) {
            return false;
        }

        let userDetails = await firebaseService.getUserDetails(user);

        if (userDetails.length === 0) {
            await firebaseService.addUser(user);
            userDetails = await firebaseService.getUserDetails(user);
        } else {
            await firebaseService.updateUser(userDetails, {
                messagingToken: token
            });
        }

        return true;
    }

    const hasRun = useRef(false);

    useEffect(() => {

        if (hasRun.current) return;
        hasRun.current = true;

        if (user && !usersActivityLoaded) {
            getActivity(true).then((data) => {
                setLoadingActivity(false);
                setUsersActivityRaw(data);

                requestPermission().then(r => {
                    updateUserMessageToken(r).then((t)=>{
                        console.log("updated token: ", t);
                    })
                })
            });
        }
    })

    const openConfirmDialog = (activity) => {
        setConfirmDialogOpen(true);
        setActivityToRemove(activity);
    }

    const openUserInfoDialog = async (userEmail) => {
        setUserInfoDialogOpen(true);
        setUserInfoStatsToShow(null);
        setUserInfoToShow(null);

        let userDetails = await firebaseService.searchUsersByEmail(userEmail, user, true);

        if (userDetails.length === 1) {
            let user = userDetails[0];
            setUserInfoToShow(user);

            let usersLibrary = await firebaseService.getUserLibrary(user);
            let numberOfBooks = usersLibrary.length;
            let numberOfCompletedBooks = usersLibrary.filter((book) => book.completed).length;
            let numberOfBooksReading = usersLibrary.filter((book) => book.currentlyReading).length;
            let numberOfBooksUnread = usersLibrary.filter((book) => !book.completed && !book.currentlyReading).length;

            setTimeout(() => {
                setUserInfoStatsToShow({
                    numberOfBooks: numberOfBooks.toString(),
                    numberOfCompletedBooks: numberOfCompletedBooks.toString(),
                    numberOfBooksReading: numberOfBooksReading.toString(),
                    numberOfBooksUnread: numberOfBooksUnread.toString()
                });
            }, 1000)
        }
    }

    const followsMe = (person) => {
        let followsMe = false;
        let following = person.following ? person.following : [];
        if (following.length > 0) {
            following.forEach((item) => {
                let i = JSON.parse(item);
                if (i.email === user.email) {
                    followsMe = true;
                }
            })
        }
        return followsMe;
    }

    const ownsActivity = (activity) => {
        return activity.userEmail === user.email;
    }

    const getNiceDate = (date) => {
        let dateStr = "";
        const dateObj = new Date(date.toDate());
        const now = new Date();
        const diff = now.getTime() - dateObj.getTime();
        const diffMinutes = Math.round(diff / 60000);

        if (diffMinutes < 1) {
            return "Just now";
        } else if (diffMinutes === 1) {
            return diffMinutes + " minute ago";
        } else if (diffMinutes < 60) {
            return diffMinutes + " minutes ago";
        }

        const dateOptions = {year: "numeric", month: "long", day: "numeric", hour: "numeric", minute: "numeric"};
        dateStr = dateObj.toLocaleDateString("en-US", dateOptions);
        return dateStr;
    }

    const truncateAuthors = (authors) => {
        let authorArray = authors.split(", ");
        if (authorArray.length > 2) {
            return authorArray[0] + ", " + authorArray[1] + ", et al.";
        } else if (authorArray.length > 1) {
            return authorArray[0] + ", et al.";
        } else {
            return authorArray[0];
        }
    }

    return (
        <>
            <NavBar/>
            <Container className="flex-grow-1 mt-5">
                <div className="activity-page">
                    <h4>Recent Activity</h4>

                    {loadingActivity &&
                        <Loading/>
                    }

                    <div className="activity-container">

                        {(usersActivityRaw.length === 0 && !loadingActivity) &&
                            <div className="no-activity">
                                There's nothing here...
                            </div>
                        }

                        <List>
                            {usersActivityRaw.length > 0 && (
                                <>
                                    {usersActivityRaw.map((group, key) => (
                                        <div className="activity-group" key={key}>
                                            <div className="activity-group-date">
                                                <div className="text">{group.date}</div>
                                            </div>

                                            {group.activities.map((activity) => (
                                                <ListItem alignItems="flex-start" key={activity.id}
                                                          className="activity-item"
                                                          secondaryAction={(ownsActivity(activity)) &&
                                                              <IconButton edge="end" aria-label="comments"
                                                                          className="activity-button"
                                                                          onClick={() => {
                                                                              openConfirmDialog(activity)
                                                                          }}>
                                                                  <FontAwesomeIcon icon="trash"/>
                                                              </IconButton>
                                                          }>
                                                    <ListItemAvatar>
                                                        <OnlineTag person={activity}/>
                                                        <AvatarGroup spacing="small">
                                                            <Avatar alt={activity.userName} src={activity.userAvatarURL}/>
                                                            <Avatar alt={activity.bookTitle} src={activity.bookCoverData}/>
                                                        </AvatarGroup>
                                                    </ListItemAvatar>
                                                    <ListItemText
                                                        className="main-text"
                                                        primary={activity.userName}
                                                        onClick={() => openUserInfoDialog(activity.userEmail)}
                                                    />
                                                    <div className="secondary-text">
                                                        — {activity.activityDetail} <span
                                                        className="title">{activity.bookTitle}</span> by <span
                                                        className="author">{truncateAuthors(activity.bookAuthor)}</span>
                                                    </div>
                                                    {(activity.rating !== '0') &&
                                                        <ListItemText
                                                            className="rating"
                                                            secondary={
                                                                <Rating
                                                                    name="simple-controlled"
                                                                    value={activity.rating}
                                                                    disabled
                                                                />
                                                            }
                                                        />
                                                    }
                                                    <ListItemText
                                                        className="date-text"
                                                        secondary={getNiceDate(activity.activityDate)}
                                                    />
                                                    {(activity.new) &&
                                                        <span className="new-activity-badge">
                                                            <Chip label="New" className="chip"/>
                                                    </span>
                                                    }
                                                </ListItem>
                                            ))}

                                        </div>


                                    ))}
                                </>
                            )}
                        </List>
                    </div>

                </div>
            </Container>

            <Dialog fullWidth className="confirm-remove-dialog" open={confirmDialogOpen} onClose={() => {
                setConfirmDialogOpen(false)
            }}>
                {activityToRemove &&
                    <>
                        <DialogTitle>Warning</DialogTitle>
                        <DialogContent>Are you sure you want to remove this activity?</DialogContent>
                        <DialogActions className="dialog-actions">
                            <Button className="dialog-button" color="warning" onClick={() => {
                                deleteActivity(activityToRemove.id)
                            }}>Yes</Button>
                            <Button className="dialog-button" color="primary" onClick={() => {
                                setConfirmDialogOpen(false)
                            }}>No</Button>
                        </DialogActions>
                    </>
                }
            </Dialog>

            <Dialog fullWidth className="user-info-dialog" open={userInfoDialogOpen} onClose={() => {
                setUserInfoDialogOpen(false)
                setUserInfoStatsToShow(null)
                setTimeout(() => {
                    setUserInfoToShow(null);
                }, 200)
            }}>
                {(userInfoToShow) ?
                    <div className="user-info-dialog-inner">
                        <DialogTitle>
                            <Avatar src={userInfoToShow.avatarURL} alt={userInfoToShow.name} className="user-avatar"/>
                            <div className="name-and-email">
                                <span className="user-name">
                                    {userInfoToShow.name}
                                    {followsMe(userInfoToShow) &&
                                        <Chip label="Follows you" className="follows-user-badge"/>
                                    }
                                </span>
                                <span className="user-email">{userInfoToShow.email}</span>
                            </div>
                        </DialogTitle>
                        <DialogContent>
                            <div className="inner">
                                <div className="info last-online">
                                    <span className="label">Last online</span>
                                    <span className="value">{getNiceDate(userInfoToShow.lastOnline)}</span>
                                </div>
                                <div className="info member-since">
                                    <span className="label">Member since</span>
                                    <span className="value">{getNiceDate(userInfoToShow.memberSince)}</span>
                                </div>

                                <div className="profile-stats">
                                    <Tooltip title="In their library">
                                        <Chip icon={<FontAwesomeIcon icon="box-open"/>}
                                              label={(userInfoStatsToShow?.numberOfBooks) ? userInfoStatsToShow.numberOfBooks : <Loading/>}
                                              className="chip total-chip"/>
                                    </Tooltip>
                                    <Tooltip title="Unread">
                                        <Chip icon={<FontAwesomeIcon icon="book"/>}
                                              label={(userInfoStatsToShow?.numberOfBooksUnread) ? userInfoStatsToShow.numberOfBooksUnread : <Loading/>}
                                              className="chip unread-chip"/>
                                    </Tooltip>
                                    <Tooltip title="Reading">
                                        <Chip icon={<FontAwesomeIcon icon="book-open"/>}
                                              label={(userInfoStatsToShow?.numberOfBooksReading) ? userInfoStatsToShow.numberOfBooksReading : <Loading/>}
                                              className="chip reading-chip"/>
                                    </Tooltip>
                                    <Tooltip title="Completed">
                                        <Chip icon={<FontAwesomeIcon icon="check"/>}
                                              label={(userInfoStatsToShow?.numberOfCompletedBooks) ? userInfoStatsToShow.numberOfCompletedBooks : <Loading/>}
                                              className="chip completed-chip"/>
                                    </Tooltip>
                                </div>

                            </div>
                        </DialogContent>
                        <DialogActions className="dialog-actions">
                            <Button className="dialog-button" color="primary" onClick={() => {
                                setUserInfoDialogOpen(false)
                                setUserInfoToShow(null)
                            }}>Close</Button>
                        </DialogActions>
                    </div>
                    : <Loading/>
                }
            </Dialog>

            <Footer/>
        </>
    );
};

export default withAuthenticationRequired(Activity, {
    onRedirecting: () => <Loading/>,
});
